import { useCallback, useEffect, useState } from "react";
import { Mail, MessageSquare } from "lucide-react";
import api from "../lib/apiClient";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().replace("T", " ").slice(0, 16) + " UTC"; }
  catch { return iso; }
}

export const ContactPanel = ({ refreshing }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setData((await api.get("/admin/contact-messages")).data); }
    catch { setError("Couldn't load contact messages. Please try again."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!refreshing) load(); }, [refreshing, load]);

  return (
    <section className="card-soft rounded-2xl overflow-hidden mb-8" data-testid="contact-panel">
      <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-5 border-b border-border">
        <div>
          <span className="eyebrow flex items-center gap-2"><MessageSquare size={14} /> Inbox</span>
          <h2 className="font-display font-bold text-lg mt-2" data-testid="contact-panel-heading">Contact messages</h2>
          <p className="text-sm text-muted-foreground mt-1" data-testid="contact-panel-count">
            {loading ? "Loading messages…" : error ? "Messages unavailable" : `${data.total} message${data.total === 1 ? "" : "s"} received`}
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="rounded-xl h-11 px-5 shrink-0" data-testid="contact-panel-refresh">Refresh</Button>
      </div>

      {error ? (
        <div className="p-6" role="alert" data-testid="contact-panel-error">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" onClick={load} className="mt-3" data-testid="contact-panel-retry">Try again</Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="contact-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-[#f6f1e6]">
              <th className="px-6 py-3 font-bold">From</th>
              <th className="px-6 py-3 font-bold hidden md:table-cell">Message</th>
              <th className="px-6 py-3 font-bold hidden sm:table-cell">Received (UTC)</th>
              <th className="px-6 py-3"></th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-6 py-8 text-muted-foreground" data-testid="contact-loading">Loading messages…</td></tr>
              ) : !data.recent.length ? (
                <tr><td colSpan={4} className="px-6 py-10 text-center text-muted-foreground" data-testid="contact-empty">No messages yet. Submissions from the contact form will appear here.</td></tr>
              ) : (
                data.recent.map((row) => (
                  <tr key={row.id} data-testid={`contact-row-${row.id}`} className="border-b border-border last:border-0 hover:bg-[#f9f6ef] transition-colors">
                    <td className="px-6 py-4"><p className="font-semibold">{row.name}</p><p className="text-xs text-muted-foreground break-all">{row.email}</p></td>
                    <td className="px-6 py-4 hidden md:table-cell text-muted-foreground max-w-md truncate">{row.message}</td>
                    <td className="px-6 py-4 hidden sm:table-cell text-muted-foreground whitespace-nowrap">{fmt(row.created_at)}</td>
                    <td className="px-6 py-4 text-right"><button onClick={() => setSelected(row)} data-testid={`view-contact-${row.id}`} className="font-semibold text-xs hover:underline" style={{ color: "var(--gold)" }}>View</button></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-[#f4f1e9] border-l border-border overflow-y-auto" data-testid="contact-detail-drawer">
          <SheetHeader><SheetTitle className="font-display font-bold">Contact message</SheetTitle><SheetDescription className="text-xs text-muted-foreground">Message submitted through the website contact form.</SheetDescription></SheetHeader>
          {selected && (
            <div className="mt-6 space-y-5">
              <div className="card-soft rounded-xl p-4">
                <p className="font-semibold text-lg">{selected.name}</p>
                <p className="flex items-center gap-2 break-all text-sm text-muted-foreground mt-2"><Mail className="w-4 h-4 shrink-0" /> {selected.email}</p>
                <p className="text-xs text-muted-foreground mt-3">Received {fmt(selected.created_at)}</p>
                <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold hover:underline" style={{ color: "var(--gold)" }} data-testid="contact-reply"><Mail className="w-3.5 h-3.5" /> Reply by email</a>
              </div>
              <div>
                <p className="eyebrow mb-1.5">Message</p>
                <p className="text-sm whitespace-pre-wrap break-words card-soft rounded-xl p-3 text-foreground/90">{selected.message}</p>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
};
